import { Injectable } from '@angular/core';
import { GenesysCloudService } from './genesys-cloud.service';
import { RootScopeService } from './root-scope.service';
import * as Constants from '../models/Constants';
import { TimerService } from './timer.service';
import { CustBusService} from './customer-search-bussiness-logic.service';


@Injectable({
  providedIn: 'root'
})
export class MultipleAccountService {

  constructor(public rootScope: RootScopeService,
     public genesysService: GenesysCloudService,
     public customerService: CustBusService,
     public timerService: TimerService) { }

  
  /**
   * Handles disconnecting the previous live account and prepares the system for a new customer account.
   * Updates interaction statuses, clears timers, resets attributes, and launches the new account tab.
   * @param data - Contains information about the new customer/account to activate.
   * CCR3-13122
   */
  handlePreviousLiveAccountDisconnect(data) {
    try {
      // Disconnect all active calls and update their status
      this.rootScope.customerTabs.forEach(tab => {
      // Handle active call tabs (not additional accounts)
      if (tab.activeCall && !tab.additionalAccount) {
        tab.interactionDisconnected = true;
        tab.isMultipleAccountTabOpened = true;

        // Add previous account record if not present
        if (tab.multiAccountRecord.length === 0) {
        tab.multiAccountRecord.push({
          accountName: tab.title,
          startTime: tab.startTime,
          endTime: new Date().toISOString(),
          udpId: tab.id || '',
        });
        }

        // Add new account record
        tab.multiAccountRecord.push({
        accountName: `${data.lastName},${data.firstName}`,
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString(),
        udpId:data.udpId
        });

        tab.domainIndex = {
        domainId: Constants.navMenu.custOverview,
        domainName: Constants.domainCard.about,
        };

        this.updateMultiAccountsMap(tab.id);
        this.timerService.stopTimer(tab.customerSessionId);
        this.genesysService.clearLiveCallInterval();
      }
      // Handle simulation mode or customer overview tab (no active call, not additional account)
      else if (
        tab.conversationId === this.rootScope.conversationId &&
        !tab.additionalAccount
      ) {
       
        tab.multiAccountRecord.push({
        accountName: `${data.lastName},${data.firstName}`,
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString(),
        udpId: data.udpId
        });

        let custUpdAddPayload = {

          'customerSessionId': tab?.customerSessionId,
          'cusTab': {

            'isMultipleAccountTabOpened': tab?.isMultipleAccountTabOpened,
            'multiAccountRecord': tab?.multiAccountRecord,
          }
        }
        this.customerService.updateCustomerInElasticCache(custUpdAddPayload);
      }

      // Always close active calls
      if (tab.activeCall) {
        tab.activeCall = false;
        tab.active = false;
        this.customerService.closeCustomerTbaScreens(tab); // CCR3-13194
      }
      });

      // Genesys call object and user/participant logic
      const callObj = this.rootScope.genesysCallObj?.eventBody;
      const userId = localStorage.getItem('userId');
      let activeUser = callObj?.participants?.find(x => x?.user?.id === userId);
      const liveCustomer = callObj?.participants?.find(
      x => x?.purpose === 'customer' && x?.state !== 'terminated'
      );

      // For simulation mode, use liveCustomer if activeUser not found
      if (this.rootScope.simulationData?.isSimulationModeActive && !activeUser) {
      activeUser = liveCustomer;
      }

      // Update event data for active user
      if (activeUser) {
      activeUser.endTime = new Date().toISOString();
      this.genesysService.updateTelephonyEventData(
        this.rootScope.genesysCallObj,
        Constants.logEvent.DISCONNECT
      );
      this.genesysService.captureInteractionEventData(
        activeUser,
        this.rootScope.genesysCallObj
      );
      }

      // Update attributes and launch new account tab for live customer
      if (liveCustomer && liveCustomer.attributes) {
      const attrs = liveCustomer.attributes;
      Constants.ALIS_ATTRIBUTE_CALL.forEach(key => {
        if (attrs[key]) attrs[key] = '';
      });

      attrs['UDP_GBL_ID'] = data?.udpId;
      attrs['CS_SECURITY_LEVEL'] = '2';
      attrs['PINV'] = 'U';
      attrs['CUSTOMER_TYPE'] = 'ACH';

      this.rootScope.patchGENAttribute.next({
        conversationId: this.rootScope.conversationId,
        participantId: liveCustomer.id,
        body: { attributes: attrs }
      });

      this.updateMultiAccountsMap(data?.udpId);

      // Handle simulation records
      if (this.rootScope.simulationData?.isSimulationModeActive) {
        this.rootScope.simulationRecords.splice(0, 1);
        this.rootScope.simulationRecords.push({
        fullName: `${data?.lastName}, ${data?.firstName}`,
        channel: Constants.conversion.call,
        conversationAttributes: attrs,
        conversationId: this.rootScope.conversationId,
        startTime: new Date()
        });
      }

      this.genesysService.launchCustomerAccountTab(liveCustomer);
      this.timerService.startTimerAtCallLand(new Date().toISOString());

      if (this.rootScope.checkMaxCustAccount()) {
        this.genesysService.updateChatTabStatus(false);
      }
      }
    } catch (error) {
      console.error('Error in handlePreviousLiveAccountDisconnect:', error);
    }
  }

  /**
   * Updates the multiAccountsMap by adding the specified udpId to the list of accounts
   * associated with the current conversationId, if it is not already present.
   *
   * @param udpId - The unique identifier of the account to add to the multiAccountsMap.
   * CCR3-13122
   */
  updateMultiAccountsMap(udpId: string) {
    const convId = this.rootScope.conversationId;
    const accounts = this.rootScope.multiAccountsMap.get(convId) || [];
    if (!accounts.includes(udpId)) {
      accounts.push(udpId);
      this.rootScope.multiAccountsMap.set(convId, accounts);
    }
  }

    /**
     * Validates the wrap-up data for a given tab based on interaction type, mode, comments, and wrap-up topics.
     *
     * The validation logic checks for the presence of required fields in `tab.wrapUpData`:
     * - `interactionTypeVal`
     * - `interactionMode`
     * - `comments`
     * - At least one item in `wrapupTopicList`
     * CCR3-13182
     * Additional conditional checks are performed based on the interaction mode and customer type:
     * - If the interaction mode is 'Outbound' and the customer type is `Constants.TP`, both `interactionModeSubType` and `thirdParty` must be present.
     * - If the customer type is `Constants.TP` (but not 'Outbound'), `thirdParty` must be present.
     * - If the interaction mode is 'Outbound' (but not `Constants.TP`), `interactionModeSubType` must be present.
     * - In all other cases, returns `true` if the required fields are present.
     *
     * @param tab - The tab object containing wrap-up data and customer information.
     * @returns `true` if the wrap-up data is valid according to the business rules; otherwise, `false`.
     */
    wrapUpValidation(tab): boolean {
  
      if (tab?.wrapUpData.interactionTypeVal
        && tab?.wrapUpData.interactionMode 
        && tab?.wrapUpData.comments
        && tab?.wrapUpData.wrapupTopicList.length > 0) {
        if (tab?.wrapUpData.interactionMode == 'Outbound' && tab?.customerType == Constants.TP) {
          if (tab.interactionModeSubType && tab?.wrapUpData?.thirdParty)
  
            return true;
          else
            return false;
        }
        else if (tab?.customerType == Constants.TP) {
          if (tab?.wrapUpData.thirdParty)
            return true;
          else
            return false;
        }
        else if (tab?.wrapUpData.interactionMode == 'Outbound')
          if (tab?.wrapUpData.interactionModeSubType)
            return true;
          else
            return false;
        else
          return true;
  
      }
      else return false;
    
    }

  

      //  CCR3-13146
  /**
   * Handles the logic for managing multiple accounts associated with the current conversation.
   * - If the number of accounts linked to the active conversation reaches the maximum allowed (`MAX_MULTIPLE_ACCOUNTS`),
   *   it opens a warning dialog to inform the user.
   * - Otherwise, it opens a dialog to allow the user to add or manage another account.
   *
   * The dialog content and behavior are determined by the current state and configuration constants.
   *
   * In summary, this method handles the action for adding or managing multiple accounts for the current conversation:
   * it shows a warning dialog if the maximum allowed accounts are already linked; otherwise, it opens the dialog
   * to add or manage another account.
   *
   * @remarks CCR3-13122
   * This method relies on `rootScope` properties and methods, including `multiAccountsMap`, `activeCustTab`, `CCTConfigs`,
   * and `openDialog`. It also uses constants from the `Constants` object.
   */
  handleMultipleAccountAction(selectedRow?) {
    const conversationId = this.rootScope.conversationId;
    const accounts = this.rootScope.multiAccountsMap.get(conversationId) || [];
    const maxAccounts = this.rootScope.CCTConfigs.MAX_MULTIPLE_ACCOUNTS;

    if (accounts.length === maxAccounts) {
      this.rootScope.openDialog({
        title: '',
        subheading: '',
        message: Constants.MAX_MULTIPLE_ALERT_WARNING,
        firstbutton: '',
        secondbutton: 'Got it',
        crossIcon: false,
        comingFrom: '',
        additionalClass: 'multiple-account-modal',
      });
    }
    else {

      let isAnyActiveCall = this.rootScope.customerTabs.find(tab => tab.activeCall && !tab.additionalAccount);
      if (isAnyActiveCall && !this.wrapUpValidation(isAnyActiveCall)) {

        this.rootScope.openDialog({
          title: '',
          subheading: '',
          message: Constants.MAX_MULTIPLE_ALERT,
          firstbutton: '',
          secondbutton: 'Got it',
          crossIcon: false,
          comingFrom: Constants.buttonEnum.multipleAccount,
          additionalClass: 'multiple-account-modal',
          rowData: isAnyActiveCall
        });
      }
      else {
        this.handlePreviousLiveAccountDisconnect(selectedRow);
      }

    } 
    
  }
}
